"use client";
import {forwardRef, JSX, ReactNode} from "react";

import {useSectionSpace} from "@/sections/use-section-space";
import type {SpaceProps} from "@/sections/use-section-space";

import {RentalCard} from "@/entities/rental";
import type {RentalCardProps} from "@/entities/rental";

import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {ILinkWithTarget} from "@/shared/ui/buttons/types";
import {Typography} from "@/shared/ui/data-display/typography";
import {SpinnerOverlay} from "@/shared/ui/feedback/spinner-overlay";
import {ContainerBase} from "@/shared/ui/layout/container-base";

import "./styles/rental-offer.scss";

export interface RentalOfferContent {
    title?: string;
    subtitle?: string;
}

export interface RentalOfferProps {
    properties: Array<RentalCardProps>;
    loading: {
        isFiltering: boolean;
        isInitial: boolean;
    };
    space?: SpaceProps;
    content?: RentalOfferContent;
    viewAllLink?: ILinkWithTarget;
    anchorId?: string;
    children?: ReactNode;
    disableSlice?: boolean;
    headerExtra?: ReactNode;
}

export const RentalOffer = forwardRef<HTMLElement, RentalOfferProps>(function RentalOffer(
    {properties, loading, space, content, viewAllLink, anchorId, children, disableSlice, headerExtra},
    ref,
): JSX.Element {
    const sectionVars = useSectionSpace(space);
    const isMobileViewport = useMatchMedia({maxWidth: 767});
    const {t} = useTranslation("common");
    const {t: tSearch} = useTranslation("search");

    const visibleProperties = !disableSlice && isMobileViewport ? properties.slice(0, 2) : properties;

    return (
        <section ref={ref} className="rental-offer" style={sectionVars} id={anchorId}>
            <ContainerBase>
                <div className="rental-offer__container">
                    <div className="rental-offer__header rental-offer__header_notbreak">
                        <div className="rental-offer__header-main">
                            {content?.title && (
                                <Typography as="h2" size={{desktop: 24, mobile: 18}} className="rental-offer__title rental-offer__description_left">
                                    {content.title}
                                </Typography>
                            )}
                            {content?.subtitle && (
                                <Typography
                                    as="p"
                                    color="paragraph"
                                    weight={400}
                                    size={{desktop: 14, mobile: 12}}
                                    className="rental-offer__description rental-offer__description_left"
                                >
                                    {content.subtitle}
                                </Typography>
                            )}
                        </div>
                        {(viewAllLink?.url || headerExtra) && (
                            <div className="rental-offer__header-actions">
                                {headerExtra}
                                {viewAllLink?.url && (
                                    <ButtonBase
                                        variant="secondary-outline"
                                        size="sm"
                                        width="auto"
                                        as="link"
                                        href={viewAllLink.url}
                                        target={viewAllLink.target}
                                    >
                                        {viewAllLink.title || t("viewAll")}
                                    </ButtonBase>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="rental-offer__cards" style={{position: "relative"}}>
                        <SpinnerOverlay loading={loading.isFiltering} size="large" variant="overlay" zIndex={8} />
                        {!loading.isInitial && !loading.isFiltering && visibleProperties.length === 0 && (
                            <div className="rental-offer__empty">
                                <Typography size={32} weight={700} color="black" as="h3">
                                    {tSearch("moreIsOutThere")}
                                </Typography>

                                <Typography size={16} lineHeight={150} color="paragraph" as="p">
                                    {tSearch("noHomesMatch")}
                                </Typography>
                            </div>
                        )}
                        {visibleProperties.map((property) => (
                            <RentalCard key={property.id} {...property} />
                        ))}
                    </div>
                </div>

                {children}
            </ContainerBase>
        </section>
    );
});
